import { PLATFORM_RELEASE } from '@/domains/core/settings/constants';

import { IS_LOCAL } from '../platform/constants';

import { getBrowserInfo } from './utils/getBrowserInfo';

const importSentry = () => import('@sentry/browser');
let isInitialized = false;

export async function loadSentry(beforeInitCallback?: () => any) {
  if (typeof window === 'undefined') {
    throw Error(
      'Sentry is not available on server side. Please use Datadog tracing.'
    );
  }

  const { name, version } = getBrowserInfo();
  const isManagedBrowser =
    (name === 'Opera' && version >= 16) ||
    (name === 'Chrome' && version >= 50) ||
    (name === 'Edge' && version >= 12) ||
    (name === 'Firefox' && version >= 27) ||
    (name === 'Safari' && version >= 11) ||
    name === 'browserTest' ||
    name === 'ManoManoMobileApp';

  /*
   * Sentry settings are located in Spartacux Gitlab settings as we need to
   * upload source maps at build time.
   *
   * @see https://git.manomano.tech/solutions/spartacux/-/settings/ci_cd
   */
  const dsn = process.env.NEXT_PUBLIC_SENTRY_DSN;
  const enabled = process.env.NEXT_PUBLIC_SENTRY_ENABLED === 'true';

  if (!isManagedBrowser || (IS_LOCAL && !enabled)) {
    return Promise.resolve({
      // eslint-disable-next-line no-console
      captureException: console.error,
      // eslint-disable-next-line no-console
      captureMessage: console.warn,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      flush: (arg: number) => {},
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      withScope: (fn: (scope: any) => void) => {
        fn({
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          addEventProcessor: (cb: (scope: any) => void) => {},
          setExtra: () => {},
          setLevel: () => {},
          setTag: () => {},
        });
      },
    } as typeof import('@sentry/browser'));
  }

  if (!enabled) {
    throw Error('Sentry is not enabled.');
  }

  if (!dsn) {
    throw Error('Sentry is not configured.');
  }

  const environment =
    // eslint-disable-next-line no-underscore-dangle
    window?.__NEXT_DATA__?.props?.pageProps?.appSettings?.PLATFORM_ENVIRONMENT;

  return importSentry().then((Sentry) => {
    if (!isInitialized) {
      if (beforeInitCallback) {
        beforeInitCallback();
      }

      // We do not add the CaptureConsole integrations since we implement it on our side
      Sentry.init({
        dsn,
        environment,
        release: PLATFORM_RELEASE,
      });
      isInitialized = true;
    }

    return Sentry;
  });
}
